<template>
  <div class="orderDetails">
    <section class="formWrapper mb-5">
      <div class="formWrapper__header">
        <Icon icon="order-details" class="formWrapper__header__icon" />
        <h3>Szczegóły zamówienia</h3>
      </div>
      <div class="formWrapper__inner">
        <div class="orderDetails__invoice">
          <div>
            <h4>Dane do faktury</h4>
            <address>
            {{fvForm.companyName}}<br>
            ul. {{fvForm.street}} {{fvForm.streetNumber}} {{fvForm.localNumber}}<br>
            {{fvForm.zipCode}} {{fvForm.city}}<br>
            NIP: {{fvForm.nip}}<br>
            Nr rachunku bankowego: <br>
            {{bankNumber.accountNumber}}
            </address>
          </div>
          <div>
            <h4>Osoba kontaktowa</h4>
            <address>
            {{contactPerson.name}} {{contactPerson.surname}}<br>
            email: <a v-bind:href="'mailto:'+contactPerson.email">{{contactPerson.email}}</a>
            </address>
          </div>
        </div>
      </div>
    </section>
    <section class="formWrapper mb-5">
      <div class="formWrapper__header">
        <Icon icon="delivery" class="formWrapper__header__icon" />
        <h3>{{ isProductEVoucher || isProductVirtualNDE ? 'Dane do realizacji zamówienia' : 'Dane dostawy' }}</h3>
      </div>
      <div class="formWrapper__inner">
        <div class="orderDetails__delivery" v-for="(address, index) in delivery.address" :key="index" >
          <fragment v-if="!isProductEVoucher && !isVirtualCard" >
            <div>
              <h4>Adres dostawy {{index + 1}}</h4>
              <address>
                {{address.companyName}}<br>
                ul. {{address.street}} {{address.streetNumber}} {{address.localNumber}}<br>
                {{address.zipCode}} {{address.city}}
              </address>
              <p v-if="hasDeliveryFile(address.delivery_file)">
                Pliki: <a :href="`${getUrl}${address.token}`" target="_blank">Pobierz plik</a>
              </p>
            </div>
            <div v-if="!isProductEVoucher">
              <div>
                <h4>Osoba upoważniona 1</h4>
                <address>
                {{address.personOneName}} {{address.personOneSurname}}<br>
                tel. {{address.personOnePhone}}
                </address>
              </div>
              <div v-if="address.personSecondName !== ''" class="secondContactPerson">
                <h4>Osoba upoważniona 2</h4>
                <address>
                {{address.personSecondName}} {{address.personSecondSurname}}<br>
                tel. {{address.personSecondPhone}}
                </address>
              </div>
            </div>
          </fragment>
        </div>
        <div class="orderDetails__delivery">
          <div v-if="isProductEVoucher || isProductVirtualNDE">
            <h4>Dane na które wyślemy raport doręczeń</h4>
            <address>
              <p> email: {{contactPerson.email}} </p>
              <p> nr telefonu: {{contactPerson.phoneNumber}} </p>
            </address>
            <p v-if="isProductVirtualNDE">Na ten numer telefonu otrzymasz hasło do otwarcia pliku z raportem doręczeń.</p>
          </div>
          <div v-else>
            <h4>{{ isVirtualCard ? 'Email do realizacji zamówienia' : 'Email do powiadomień'}}</h4>
            <address>
              {{contactPerson.otherEmail.length > 0 ? contactPerson.otherEmail : contactPerson.email}}
            </address>
          </div>
        </div>
      </div>
    </section>
    <section class="summaryWrapper formWrapper mb-5">
      <div class="formWrapper__header">
        <Icon icon="order-summary" class="formWrapper__header__icon" />
        <h3>Podsumowanie zamówienia</h3>
      </div>
      <div class="formWrapper__inner">
        <Summary v-if="custommerContractLoaded" :with-products="summaryVisibility !== summaryVisibilityOptions.HIDE"></Summary>
      </div>
    </section>
    <section class="payment formWrapper mb-5" v-if="!isOnlinePaymentDisabled">
      <div class="formWrapper__header">
        <Icon icon="payment" class="formWrapper__header__icon" />
        <h3>Metoda płatności</h3>
      </div>
      <div class="formWrapper__inner">
        <CustomAlert type="info" v-if="onlinePayment === 'true'">
          <p class="mb-0">Po kliknięciu "Zamawiam i płacę" nastąpi przekierowanie na stronę operatora płatności,
            gdzie w bezpiecznym środowisku możliwe będzie opłacenie zamówienia.</p>
        </CustomAlert>
        <div class="payment__inputs">
          <div class="radio-inline">
            <input class="dl-radio-selected" type="radio" id="payment1" v-model="onlinePayment" value=false
                          @change="updateOnlinePayment">
            <label for="payment1"><span>Płatność proforma</span><br /><span>0.00 zł</span></label>
          </div>
          <div class="radio-inline" :class="{ disabled: isOnlinePaymentDisabled }" v-if="isOnlinePaymentVisible">
            <input class="dl-radio-selected" type="radio" id="payment2" v-model="onlinePayment" value=true
                          @change="updateOnlinePayment" :disabled="isOnlinePaymentDisabled">
            <label for="payment2"><span>Płatność on-line</span><br /><span>7.50 zł (9.23 zł brutto)</span></label>
          </div>
        </div>
        <CustomAlert type="info" v-if="isOnlinePaymentDisabled" class="mt-4 mb-0">
          <p class="mb-0">
            Płatność online jest chwilowo niedostępna z przyczyn technicznych. Jesteśmy w trakcie rozwiązywania problemu - dziękujemy za cierpliwość i przepraszamy za niedogodności!
          </p>
        </CustomAlert>
      </div>
    </section>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import Summary from '../Summary/Summary.vue';
import { checkProductsIsVirtualCard } from '@/utils/categoryUtils';
import Icon from '../Icon.vue';
import CustomAlert from '../CustomAlert.vue';
import { summaryVisibilityOptions } from '@/utils/constants/summary';

const MAX_ONLINE_PAYMENT_GROSS = 20000;

export default {
  name: 'OrderDetails',
  components: { Summary, Icon, CustomAlert },
  data() {
    return {
      onlinePayment: false,
    };
  },
  computed: {
    ...mapState({
      fvForm: state => state.fvForm,
      bankNumber: state => state.bankNumber,
      contactPerson: state => state.contactPerson,
      delivery: state => state.delivery,
      custommerContractLoaded: state => state.customer.loaded,
      summaryVisibility: state => state.customer.contract.summary_option,
    }),
    getUrl() {
      return `${process.env.VUE_APP_BASE_URL}/api/v1/file/download/`;
    },
    isProductEVoucher() {
      return this.$store.getters.isProductEVoucher;
    },
    isProductVirtualNDE() {
      return this.$store.getters.isProductVirtualNDE;
    },
    isVirtualCard() {
      return checkProductsIsVirtualCard(this.$store.state.products.all.products[0]);
    },
    isOnlinePaymentVisible() {
      const total = parseFloat(this.$store.getters['cart/getGrossSummary']);
      const totalLessThanMax = total <= MAX_ONLINE_PAYMENT_GROSS;
      const isNotVirtual = !checkProductsIsVirtualCard(this.$store.state.products.all.products[0]);
      return totalLessThanMax && (isNotVirtual || this.$store.getters.isProductVirtualNDE);
    },
    isOnlinePaymentDisabled() {
      return this.$store.getters.isProductNDE;
    },
  },
  methods: {
    updateOnlinePayment() {
      this.$store.dispatch('cart/setOnline', this.onlinePayment);
    },
    hasDeliveryFile(deliveryFile) {
      if (typeof deliveryFile === 'undefined' || deliveryFile === '') return false;
      return true;
    },
  },
  created() {
    this.summaryVisibilityOptions = summaryVisibilityOptions;
    this.updateOnlinePayment();
  },
};
</script>
<style>
  input[type="radio"]{
    padding-left: 10px;
  }
  .mb-4 {
    margin-bottom: 2rem;
  }
</style>
